import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineCornerDownRightAltIcon from 'LuxKit/Icons/line/LineCornerDownRightAltIcon'
import type { MessagePart } from './Message'
import { FLIGHTS_LANDING_PAGE_CONTEXT_LABEL, unescapeContextLabel } from 'tripPlanner/utils/contextTag'

const ContextLabel = styled.div`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-size: ${rem(13)};
  max-width: 100%;
`

const ContextIcon = styled(LineCornerDownRightAltIcon)`
  vertical-align: text-bottom;
`

/**
 * Extracts the display label from the `label` attribute of a <Context> tag.
 */
function extractContextContent(text: string): string | undefined {
  const match = text.match(/<Context\s+label="([^"]*)"[^>]*>/i)
  return match?.[1] ? unescapeContextLabel(match[1]).trim() : undefined
}

interface Props {
  parts: Array<MessagePart>
  selectionLabels?: Array<string>
}

function MessageContextLabel({ parts, selectionLabels }: Props) {
  const intl = useIntl()
  const contextContent = useMemo(() => {
    const firstTextPart = parts.find(part => part.type === 'text')
    if (firstTextPart?.type !== 'text') return undefined
    return extractContextContent(firstTextPart.content)
  }, [parts])

  const localizedContextContent = useMemo(() => {
    switch (contextContent) {
      case 'Flight search result':
        return intl.formatMessage({
          id: 'KCWa94wmNeRBfCcJw8Ns',
          defaultMessage: 'Flight search result',
          description: 'Context label shown above a user message when it includes selected flight search results',
        })
      case 'Hotel search results':
        return intl.formatMessage({
          id: '8pZ5aEEqjXW9awoC2bsS',
          defaultMessage: 'Hotel search results',
          description: 'Context label shown above a user message when it includes hotel search results',
        })
      case 'LE Experience offer':
        return intl.formatMessage({
          id: 'M32MUJaIaQth7JlpGMmz',
          defaultMessage: 'LE Experience offer',
          description: 'Context label shown above a user message when it references a Luxury Escapes experience offer',
        })
      case 'LE Hotel offer':
        return intl.formatMessage({
          id: '4I3MQQpEbjiCdOrOKCet',
          defaultMessage: 'LE Hotel offer',
          description: 'Context label shown above a user message when it references a Luxury Escapes hotel offer',
        })
      case 'Trip planner action':
      case 'LE Hotel room option':
        return undefined
      case FLIGHTS_LANDING_PAGE_CONTEXT_LABEL:
        return undefined
      default:
        return contextContent
    }
  }, [contextContent, intl])

  const selectionContent = useMemo(() => {
    if (!selectionLabels?.length) return undefined
    return selectionLabels.join(', ')
  }, [selectionLabels])

  const labelText = selectionContent ?? localizedContextContent

  if (!labelText) {
    return null
  }

  return (
    <ContextLabel>
      <ContextIcon size="S" /> {labelText}
    </ContextLabel>
  )
}

export default MessageContextLabel
